import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plane, Menu, User, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import AuthModal from "@/components/site/AuthModal";
import CurrencyPicker from "@/components/site/CurrencyPicker";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";

export default function Navbar() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const links = [
    { to: "/flights/search", label: "Flights", on: settings.product_flight !== false },
    { to: "/hotels", label: "Hotels", on: settings.product_hotel !== false },
    { to: "/cars", label: "Cars", on: settings.product_car !== false },
    { to: "/packages", label: "Packages", on: settings.product_package !== false },
    { to: "/sightseeing", label: "Sightseeing", on: settings.product_sightseeing !== false },
    { to: "/contact", label: "Contact", on: true },
  ].filter(l => l.on);

  // Normalise legacy logo_urls stored as `/uploads/...` (pre-fix) by adding the
  // `/api/` prefix so kubernetes ingress routes them to the backend StaticFiles
  // mount instead of returning the frontend index.html.
  const rawLogo = settings.logo_url || "";
  const normLogo = rawLogo && !rawLogo.startsWith("http") && rawLogo.startsWith("/uploads/")
    ? `/api${rawLogo}`
    : rawLogo;
  const logoUrl = normLogo
    ? (normLogo.startsWith("http") ? normLogo : `${process.env.REACT_APP_BACKEND_URL}${normLogo}`)
    : "";

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          {logoUrl ? (
            <img src={logoUrl} alt={settings.brand_name} data-testid="nav-logo-image"
                 className="h-10 w-auto max-w-[180px] object-contain group-hover:scale-[1.03] transition-transform" />
          ) : (
            <>
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] flex items-center justify-center shadow-md shadow-teal-500/20 group-hover:scale-105 transition-transform">
                <Plane className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="leading-tight">
                <div className="font-heading font-bold text-[var(--brand-accent)] text-lg">{settings.brand_name}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-teal-600 -mt-0.5">{settings.tagline}</div>
              </div>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="px-4 py-2 rounded-full text-sm font-medium text-slate-700 hover:text-[var(--brand-accent)] hover:bg-teal-50 transition-colors">{l.label}</Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <CurrencyPicker />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-trigger" variant="outline" className="rounded-full">
                  <User className="w-4 h-4 mr-2" /> {user.name?.split(" ")[0]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="menu-my-bookings" onClick={() => navigate("/my-bookings")}>My Bookings</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>Admin</DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="nav-signin-btn" variant="ghost" className="hidden sm:inline-flex rounded-full"
                      onClick={() => { setAuthMode("login"); setAuthOpen(true); }}>Sign in</Button>
              <Button data-testid="nav-signup-btn" className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]"
                      onClick={() => { setAuthMode("register"); setAuthOpen(true); }}>Get started</Button>
            </>
          )}
          <Sheet>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-trigger" variant="ghost" size="icon" className="md:hidden">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <div className="flex flex-col gap-2 mt-8">
                {links.map((l) => (
                  <Link key={l.to} to={l.to} className="px-3 py-3 rounded-lg hover:bg-teal-50 text-[var(--brand-accent)] font-medium">{l.label}</Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <AuthModal open={authOpen} setOpen={setAuthOpen} mode={authMode} setMode={setAuthMode} />
    </header>
  );
}
